/**
 * Frontend Access Control Utility
 * 
 * Checks user access to features based on their plan and role permissions.
 * Works in conjunction with backend access control.
 * 
 * Features are mapped to permissions via FEATURE_PERMISSION_MAP.
 * Access is granted ONLY if ALL required permissions are true.
 */

import { getRequiredPermissions, isFeatureMapped } from './featurePermissionMap';

/**
 * Check if user has access to a specific feature
 * @param {object} user - User object with features array
 * @param {string} featureKey - Feature identifier
 * @returns {boolean} True if user has access
 */
export const hasAccess = (user, featureKey) => {
  console.log('🔍 [hasAccess] Checking access:', {
    featureKey,
    hasUser: !!user,
    isDeveloper: user?.isDeveloper,
    role: user?.role,
    features: user?.features,
    modulesAccess: user?.modulesAccess
  });

  if (!user) {
    console.log('❌ [hasAccess] No user provided');
    return false;
  }

  // Developer bypass - developers have access to everything
  if (user.isDeveloper || user.role === 'developer') {
    console.log('✅ [hasAccess] Developer bypass - granting access');
    return true;
  }

  // Check if feature is mapped to permissions
  if (!isFeatureMapped(featureKey)) {
    console.warn(`[hasAccess] Feature '${featureKey}' is not mapped in FEATURE_PERMISSION_MAP. Access denied.`, {
      featureKey,
      hasUser: !!user
    });
    return false;
  }
  
  // Get required permissions for this feature
  const requiredPermissions = getRequiredPermissions(featureKey);
  if (!requiredPermissions || requiredPermissions.length === 0) {
    console.warn(`[hasAccess] Feature '${featureKey}' has no required permissions mapped. Access denied.`, {
      featureKey,
      hasUser: !!user
    });
    return false;
  }
  
  // Check user.permissions object (from backend role_permissions table)
  // user.permissions is an object mapping permission_key -> boolean
  if (user.permissions && typeof user.permissions === 'object') {
    // Check that ALL required permissions are granted
    const allPermissionsGranted = requiredPermissions.every(permissionKey => {
      return user.permissions[permissionKey] === true;
    });
    
    console.log(`[hasAccess] Permission check: ${allPermissionsGranted ? '✅' : '❌'}`, {
      featureKey,
      requiredPermissions,
      userPermissions: user.permissions,
      allGranted: allPermissionsGranted
    });
    
    if (!allPermissionsGranted) {
      const missingPermissions = requiredPermissions.filter(p => user.permissions[p] !== true);
      console.warn(`[hasAccess] Missing required permissions for feature '${featureKey}': ${missingPermissions.join(', ')}. Access denied.`, {
        featureKey,
        requiredPermissions,
        missingPermissions,
        userPermissions: user.permissions
      });
    }
    
    return allPermissionsGranted;
  }
  
  // Fallback: Check user.features array (legacy support)
  // Note: This is less precise as it doesn't check individual permissions
  if (user.features && Array.isArray(user.features)) {
    // If features array exists, check if feature is in it
    // This is a fallback for backward compatibility
    const hasAccess = user.features.includes(featureKey);
    console.log(`[hasAccess] Features array check (fallback): ${hasAccess ? '✅' : '❌'}`, {
      features: user.features,
      lookingFor: featureKey,
      note: 'Using features array fallback - prefer user.permissions object'
    });
    return hasAccess;
  }

  // No permissions object and no features array - access denied (fail closed)
  console.warn(`[hasAccess] No permissions object or features array available for feature '${featureKey}'. Access denied.`, {
    featureKey,
    hasPermissions: !!user.permissions,
    hasFeatures: !!user.features,
    isDeveloper: user.isDeveloper || user.role === 'developer'
  });
  return false;
};

/**
 * Check if user has access to any of the specified features
 * @param {object} user - User object
 * @param {string[]} featureKeys - Array of feature identifiers
 * @returns {boolean} True if user has access to at least one feature
 */
export const hasAnyAccess = (user, featureKeys) => {
  if (!featureKeys || !Array.isArray(featureKeys)) {
    return false;
  }

  return featureKeys.some(featureKey => hasAccess(user, featureKey));
};

/**
 * Check if user has access to all of the specified features
 * @param {object} user - User object
 * @param {string[]} featureKeys - Array of feature identifiers
 * @returns {boolean} True if user has access to all features
 */
export const hasAllAccess = (user, featureKeys) => {
  if (!featureKeys || !Array.isArray(featureKeys)) {
    return false;
  }

  return featureKeys.every(featureKey => hasAccess(user, featureKey));
};


/**
 * Check if user has a specific legacy role
 * @param {object} user - User object
 * @param {...string} roles - Role names to check
 * @returns {boolean} True if user has one of the specified roles
 * @deprecated Use hasAccess() with feature keys instead
 */
export const hasRole = (user, ...roles) => {
  if (!user || !user.role) {
    return false;
  }
  return roles.includes(user.role.toLowerCase());
};

/**
 * Get user's company plan (if available)
 * @param {object} user - User object
 * @returns {string|null} Plan ID or null
 */
export const getUserPlan = (user) => {
  return user?.plan || user?.companyPlan || null;
};

/**
 * Get user's role name
 * @param {object} user - User object
 * @returns {string|null} Role name or null
 */
export const getUserRole = (user) => {
  return user?.roleName || user?.role || null;
};

/**
 * Feature categories for UI organization
 */
export const FEATURE_CATEGORIES = {
  CORE: 'core',
  TRACKING: 'tracking',
  MAINTENANCE: 'maintenance',
  STORAGE: 'storage',
  ADMIN: 'admin',
  REPORTING: 'reporting',
  INTEGRATION: 'integration'
};

/**
 * Feature metadata for UI display
 */
export const FEATURE_METADATA = {
  'asset_management': {
    name: 'Asset Management',
    description: 'Create, edit, and manage assets',
    category: FEATURE_CATEGORIES.CORE,
    icon: '📦'
  },
  'live_map': {
    name: 'Live Map',
    description: 'View live warehouse maps and asset positions',
    category: FEATURE_CATEGORIES.CORE,
    icon: '🗺️'
  },
  'blueprint_editor': {
    name: 'Blueprint Editor',
    description: 'Edit warehouse blueprints and zones',
    category: FEATURE_CATEGORIES.CORE,
    icon: '📐'
  },
  'ble_tracking': {
    name: 'BLE Tracking (Legacy)',
    description: 'BLE beacon tracking system',
    category: FEATURE_CATEGORIES.TRACKING,
    icon: '📡'
  },
  'uwb_tracking': {
    name: 'UWB Tracking',
    description: 'Real-time UWB tracking for forklifts',
    category: FEATURE_CATEGORIES.TRACKING,
    icon: '📍'
  },
  'vision_tracking': {
    name: 'Vision Tracking',
    description: 'Camera-based fiducial detection',
    category: FEATURE_CATEGORIES.TRACKING,
    icon: '📷'
  },
  'vehicle_tracking': {
    name: 'Vehicle Tracking',
    description: 'GPS vehicle tracking with telemetry',
    category: FEATURE_CATEGORIES.TRACKING,
    icon: '🚗'
  },
  'repairs': {
    name: 'Repairs',
    description: 'Asset repair ticket management',
    category: FEATURE_CATEGORIES.MAINTENANCE,
    icon: '🔧'
  },
  'inspections': {
    name: 'Inspections',
    description: 'Asset inspection workflows',
    category: FEATURE_CATEGORIES.MAINTENANCE,
    icon: '🔍'
  },
  'vendor_management': {
    name: 'Vendor Management',
    description: 'Manage vendor relationships',
    category: FEATURE_CATEGORIES.MAINTENANCE,
    icon: '👥'
  },
  'file_management': {
    name: 'File Management',
    description: 'Upload and manage files',
    category: FEATURE_CATEGORIES.STORAGE,
    icon: '📁'
  },
  'admin_panel': {
    name: 'Admin Panel',
    description: 'Full administrative access',
    category: FEATURE_CATEGORIES.ADMIN,
    icon: '⚙️'
  },
  'user_management': {
    name: 'User Management',
    description: 'Manage users within company',
    category: FEATURE_CATEGORIES.ADMIN,
    icon: '👤'
  },
  'organization_management': {
    name: 'Organization Management',
    description: 'Manage organizations and warehouses',
    category: FEATURE_CATEGORIES.CORE,
    icon: '🏢'
  },
  'reports': {
    name: 'Reports',
    description: 'Generate and export reports',
    category: FEATURE_CATEGORIES.REPORTING,
    icon: '📊'
  },
  'api_access': {
    name: 'API Access',
    description: 'API keys and programmatic access',
    category: FEATURE_CATEGORIES.INTEGRATION,
    icon: '🔑'
  }
};

/**
 * Get feature metadata
 * @param {string} featureKey - Feature identifier
 * @returns {object} Feature metadata
 */
export const getFeatureMetadata = (featureKey) => {
  return FEATURE_METADATA[featureKey] || {
    name: featureKey,
    description: 'Unknown feature',
    category: FEATURE_CATEGORIES.CORE,
    icon: '❓'
  };
};

export default {
  hasAccess,
  hasAnyAccess,
  hasAllAccess,
  hasRole,
  getUserPlan,
  getUserRole,
  getFeatureMetadata,
  FEATURE_CATEGORIES,
  FEATURE_METADATA
};
